import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  LinkIcon,
  ClipboardIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline'
import { apiService } from '../../../../shared/services/api'
import type { ShortenedUrl } from '../../../../shared/lib/url-shortener'
import { useTranslation } from 'react-i18next'
import { BOOKMARK_LIST_KEY } from '../../hooks/useBookmarkMutations'

interface UrlShortenerPanelProps {
  onCopySuccess: (text: string) => void
}

/** 대시보드 상단의 링크 단축 입력 패널 */
export default function UrlShortenerPanel({
  onCopySuccess,
}: UrlShortenerPanelProps) {
  const { t } = useTranslation()
  const [url, setUrl] = useState('')
  const [customCode, setCustomCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<ShortenedUrl | null>(null)
  const [securityError, setSecurityError] = useState<{
    message: string
    threats?: string[]
    reason?: string
  } | null>(null)
  const queryClient = useQueryClient()

  const createUrlMutation = useMutation({
    mutationFn: async (data: { originalUrl: string; customCode?: string }) => {
      return await apiService.createShortUrl(data.originalUrl, {
        customCode: data.customCode || undefined,
      })
    },
    onSuccess: data => {
      setResult(data)
      setUrl('')
      setCustomCode('')
      setSecurityError(null) // Clear any previous security errors
      // Refresh the URLs list
      queryClient.invalidateQueries({ queryKey: ['urls'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      // 북마크 화면도 같은 링크를 보여준다. 여기서 비워 두면 그쪽으로
      // 옮겼을 때 방금 만든 링크가 빠진 목록이 그대로 남는다.
      queryClient.invalidateQueries({ queryKey: BOOKMARK_LIST_KEY })
    },
    onError: (error: any) => {
      console.error('링크 생성 실패:', error)

      // Check if this is a security-related error
      if (error.code === 'MALICIOUS_URL' || error.statusCode === 400) {
        setSecurityError({
          message: error.message || t('form.maliciousUrl'),
          threats: error.details?.threats || [],
          reason: error.details?.reason || '',
        })
        setResult(null) // Clear any previous result
      } else {
        // Handle other types of errors
        setSecurityError({
          message: error.message || t('form.shortenError'),
          threats: [],
          reason: '',
        })
      }
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) {
      return
    }

    setIsLoading(true)
    setSecurityError(null) // Clear previous errors when starting new request
    setResult(null) // Clear previous results

    try {
      await createUrlMutation.mutateAsync({
        originalUrl: url.trim(),
        customCode: customCode.trim() || undefined,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      onCopySuccess(text)
    } catch (err) {
      console.error('클립보드 복사 실패:', err)
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white px-6 pb-6 pt-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
        <LinkIcon className="h-[18px] w-[18px] text-primary-600 dark:text-primary-400" />
        {t('form.shortenTitle')}
      </h2>

      {/* 한 줄에 놓되 좁아지면 접힌다. 원본 URL 이 가장 길어 더 크게 자란다 */}
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-[2_1_340px] flex-col gap-1.5">
          <label
            htmlFor="url"
            className="text-[13px] font-semibold text-gray-700 dark:text-gray-300"
          >
            {t('form.originalUrl')}
          </label>
          <input
            id="url"
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com/very-long-url..."
            className="h-[46px] rounded-[10px] border border-gray-200 bg-gray-50 px-3.5 text-[15px] font-normal text-gray-900 outline-none transition focus:border-primary-600 focus:bg-white focus:ring-[3px] focus:ring-primary-100 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:bg-gray-900 dark:focus:ring-primary-900"
            required
          />
        </div>

        <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-1.5">
          <label
            htmlFor="customCode"
            className="text-[13px] font-semibold text-gray-700 dark:text-gray-300"
          >
            {t('form.shortenCustomCode')}
          </label>
          {/* 접두사를 테두리 안에 둔다. 라벨로 감싸면 접두사까지 라벨
              글자로 읽혀 읽어주는 이름이 지저분해진다 */}
          <span className="flex h-[46px] items-center overflow-hidden rounded-[10px] border border-gray-200 bg-gray-50 focus-within:border-primary-600 focus-within:bg-white focus-within:ring-[3px] focus-within:ring-primary-100 dark:border-gray-600 dark:bg-gray-900 dark:focus-within:ring-primary-900">
            <span className="pl-3.5 pr-1 text-[15px] font-normal text-gray-400">
              shorty.kr/
            </span>
            <input
              id="customCode"
              type="text"
              value={customCode}
              onChange={e => setCustomCode(e.target.value)}
              placeholder="my-custom-link"
              className="h-full min-w-0 flex-1 border-none bg-transparent pr-3.5 text-[15px] font-normal text-gray-900 outline-none dark:text-white"
            />
          </span>
        </div>

        <button
          type="submit"
          disabled={isLoading || !url.trim()}
          className="flex h-[46px] items-center gap-2 rounded-[10px] bg-primary-600 px-5 text-[15px] font-semibold text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.7)] transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-200 disabled:shadow-none dark:disabled:bg-gray-600"
        >
          {isLoading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              {t('form.creating')}
            </>
          ) : (
            <>
              <LinkIcon className="h-4 w-4" />
              {t('form.shortenSubmit')}
            </>
          )}
        </button>
      </form>

      <AnimatePresence>
        {securityError && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"
          >
            <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
              🚨 보안 경고
            </h3>

            <div className="space-y-3">
              <p className="text-sm text-red-700 dark:text-red-300">
                {securityError.message}
              </p>

              {securityError.threats && securityError.threats.length > 0 && (
                <div className="rounded-md bg-red-100 p-3 dark:bg-red-900/30">
                  <p className="mb-2 text-xs font-medium text-red-800 dark:text-red-200">
                    탐지된 위협:
                  </p>
                  <ul className="list-inside list-disc space-y-1 text-xs text-red-700 dark:text-red-300">
                    {securityError.threats.map((threat, index) => (
                      <li key={index}>{threat}</li>
                    ))}
                  </ul>
                </div>
              )}

              {securityError.reason && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  <strong>Reason:</strong> {securityError.reason}
                </p>
              )}

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => setSecurityError(null)}
                  className="rounded-md bg-red-100 px-3 py-1 text-xs text-red-800 transition-colors hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700"
                >
                  Dismiss
                </button>
                <span className="text-xs text-red-600 dark:text-red-400">
                  Please use a different URL.
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20"
          >
            <h3 className="mb-3 text-sm font-medium text-green-800 dark:text-green-200">
              ✅ 단축 링크를 만들었습니다
            </h3>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-green-700 dark:text-green-300">
                  단축 URL
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={result.shortUrl}
                    readOnly
                    className="flex-1 rounded-md border border-green-300 bg-white px-3 py-2 text-sm dark:border-green-700 dark:bg-gray-800"
                  />
                  <button
                    onClick={() => copyToClipboard(result.shortUrl)}
                    className="rounded-md p-2 text-green-600 transition-colors hover:bg-green-100 dark:hover:bg-green-800"
                    title="Copy to clipboard"
                  >
                    <ClipboardIcon className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-md p-2 text-green-600 transition-colors hover:bg-green-100 dark:hover:bg-green-800"
                    title={t('form.generateQr')}
                  >
                    <QrCodeIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
